import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Checkbox, Input, Modal, Skeleton, message } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useRecoilValue } from "recoil";
import { locationsState } from "../../../../state/atoms";
import DataKitApi from "../../../../api/DataKitApi";
import { DataKitDto } from "../../../../domain/DataKitDto";
import "./DataKitManageLocationsModal.scss";

/** Rows shown before "+ Show N more" - the mock's cut-off. */
const COLLAPSED_ROWS = 5;

interface Props {
  kit: DataKitDto;
  onClose: () => void;
  /** After a save landed, with a sentence for the toast. */
  onSaved: (summary: string) => void;
}

/** How a location reads in a list - its own name, or its city and state when it has none. */
export function locationLabel(location: { name?: string; city?: string; state?: string }): string {
  const place = [location.city, location.state].filter(Boolean).join(", ");
  return location.name || place || "Untitled location";
}

/**
 * "Locations using {kit}" - which of the account's locations this one kit is assigned to, edited
 * as a checklist (Figma 324:38410 / 324:39737).
 *
 * The Location Assignments tab answers the other question - which kits one location carries - so
 * this is the same data turned the other way round, for the moment an admin is thinking about a kit.
 * Nothing is written until Save; the diff against what is assigned today is then sent as plain
 * assign/unassign calls, so each unassign runs the same disconnect sweep the tab's own does.
 */
export function DataKitManageLocationsModal({ kit, onClose, onSaved }: Props) {
  const locations = useRecoilValue(locationsState);
  const [assigned, setAssigned] = useState<Set<string> | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [saving, setSaving] = useState(false);

  // The parent passes a fresh arrow every render; reading it through a ref keeps this fetch to once
  // per kit instead of once per render, which would also keep resetting the form under the admin.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!kit.id) return;
    DataKitApi.listAssignments(kit.id)
      .then((res) => {
        const ids = new Set(
          (res.data || []).filter((a) => a.unitType === "LOCATION").map((a) => a.unitId)
        );
        setAssigned(ids);
        setSelected(new Set(ids));
      })
      .catch(() => {
        message.error("Could not load this library's locations");
        closeRef.current();
      });
  }, [kit.id]);

  const allIds = useMemo(
    () => locations.filter((l) => l.internalId != null).map((l) => String(l.internalId)),
    [locations]
  );

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = locations
      .filter((l) => l.internalId != null)
      .filter((l) => !q || locationLabel(l).toLowerCase().includes(q))
      .sort((a, b) => locationLabel(a).localeCompare(locationLabel(b)));
    return rows;
  }, [locations, search]);

  // Searching always shows every match - collapsing a filtered list would hide the row the admin
  // just typed to find.
  const shown = search.trim() || showAll ? visible : visible.slice(0, COLLAPSED_ROWS);
  const hiddenCount = visible.length - shown.length;
  const allChecked = allIds.length > 0 && allIds.every((id) => selected.has(id));
  const someChecked = !allChecked && allIds.some((id) => selected.has(id));

  const toggle = (id: string, on: boolean) =>
    setSelected((current) => {
      const next = new Set(current);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const save = () => {
    if (!kit.id || !assigned) return;
    const kitId = kit.id;
    const added = Array.from(selected).filter((id) => !assigned.has(id));
    const removed = Array.from(assigned).filter((id) => !selected.has(id));
    if (added.length === 0 && removed.length === 0) {
      onClose();
      return;
    }
    setSaving(true);
    // One after another, not in parallel: assignment writes all touch the kit's one assignments
    // collection, and shared serialises nothing for us.
    const steps = [
      ...added.map((id) => () => DataKitApi.createAssignment(kitId, { dataKitId: kitId, unitType: "LOCATION", unitId: id })),
      ...removed.map((id) => () => DataKitApi.deleteAssignment(kitId, "LOCATION", id)),
    ];
    steps
      .reduce<Promise<unknown>>((chain, step) => chain.then(step), Promise.resolve())
      .then(() => onSaved(summaryOf(kit.name, added.length, removed.length)))
      .catch(() => message.error("Could not save these locations"))
      .finally(() => setSaving(false));
  };

  return (
    <Modal className={"clean one data-kit-manage-locations-modal"} open width={520} onCancel={onClose}>
      <header>
        <h2>Locations using {kit.name || "this library"}</h2>
      </header>
      <p className="manage-locations-sub">Changes apply immediately when you save.</p>

      {!assigned ? (
        <Skeleton active paragraph={{ rows: 5 }} />
      ) : (
        <>
          <Input
            className="manage-locations-search"
            size="large"
            placeholder="Search locations"
            prefix={<SearchOutlined style={{ color: "#999" }} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
          />
          <ul className="manage-locations-list">
            {!search.trim() && (
              <li>
                <Checkbox
                  checked={allChecked}
                  indeterminate={someChecked}
                  onChange={(e) => setSelected(e.target.checked ? new Set(allIds) : new Set())}
                >
                  All locations
                </Checkbox>
              </li>
            )}
            {shown.map((l) => {
              const id = String(l.internalId);
              return (
                <li key={id}>
                  <Checkbox checked={selected.has(id)} onChange={(e) => toggle(id, e.target.checked)}>
                    {locationLabel(l)}
                  </Checkbox>
                </li>
              );
            })}
            {visible.length === 0 && <li className="manage-locations-empty">No locations match.</li>}
          </ul>
          {hiddenCount > 0 && (
            <button type="button" className="manage-locations-more" onClick={() => setShowAll(true)}>
              + Show {hiddenCount} more
            </button>
          )}
        </>
      )}

      <footer>
        <Button size="large" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button size="large" className="primary" loading={saving} disabled={!assigned} onClick={save}>
          Save
        </Button>
      </footer>
    </Modal>
  );
}

/** "Truluck's Core removed from 3 locations" - the mock's toast, extended to cover adds. */
function summaryOf(name: string, added: number, removed: number): string {
  const n = (count: number) => `${count} location${count === 1 ? "" : "s"}`;
  if (added > 0 && removed > 0) return `${name} added to ${n(added)} and removed from ${n(removed)}`;
  if (added > 0) return `${name} added to ${n(added)}`;
  return `${name} removed from ${n(removed)}`;
}
